import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Buildings, Check, X, Lightning, Megaphone, BookOpen, GraduationCap,
  ChartBar, Sparkle, ArrowRight, ShieldCheck
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURE_LABELS = {
  'publisher.portal.access': 'Publisher Portal Access',
  'publisher.content.insights': 'Content Insights & Analytics',
  'publisher.campaigns.basic': 'Basic Campaign Tools',
  'publisher.campaigns.addon': 'Campaign Add-ons (Boosts, Spotlights)',
};

const ADDON_TYPES = [
  { code: 'featured_game', name: 'Featured Game Placement', desc: 'Prominent placement in discovery feed for your game', icon: Sparkle },
  { code: 'launch_campaign', name: 'Launch Campaign', desc: 'Coordinated promotional campaign for new game launches', icon: Megaphone },
  { code: 'expansion_spotlight', name: 'Expansion Spotlight', desc: 'Highlight new expansions across the platform', icon: BookOpen },
  { code: 'tutorial_promo', name: 'Tutorial Promotion', desc: 'Promote tutorials and setup guides in AI assistant', icon: GraduationCap },
  { code: 'regional_boost', name: 'Regional Discovery Boost', desc: 'Boost game visibility in targeted regions', icon: ChartBar },
];

export default function PublisherPlan() {
  const { publisherId } = useParams();
  const [sub, setSub] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(null);

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API_URL}/api/monetization/publisher/${publisherId}/subscription`, { withCredentials: true }),
      axios.get(`${API_URL}/api/monetization/plans/compare?tenant_type=publisher`),
      axios.get(`${API_URL}/api/monetization/publisher/${publisherId}/addons`, { withCredentials: true }),
    ]).then(([s, c, a]) => {
      setSub(s.data);
      setComparison(c.data);
      setAddons(a.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [publisherId]);

  useEffect(() => { load(); }, [load]);

  const requestAddon = async (code, name) => {
    setRequesting(code);
    try {
      await axios.post(`${API_URL}/api/monetization/publisher/${publisherId}/addons`, {
        add_on_code: code,
        name: name,
        description: `Request for ${name}`,
      }, { withCredentials: true });
      toast.success('Add-on requested! Admin will review.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setRequesting(null); }
  };

  if (loading) return (
    <div data-testid="publisher-plan-page">
      <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Unbounded' }}>Plan & Billing</h2>
      <div className="h-48 bg-white/[0.03] rounded-xl animate-pulse" />
    </div>
  );

  const isActive = sub?.status === 'active';
  const planCode = sub?.plan_code;
  const plans = comparison?.plans || [];
  const features = comparison?.features || [];
  const planDef = plans[0]; // publisher_standard is the only plan

  return (
    <div data-testid="publisher-plan-page">
      <div className="mb-8">
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Plan & Billing</h2>
        <p className="text-[13px] text-[#555] mt-1">Your publisher subscription and campaign add-ons</p>
      </div>

      {/* Current Subscription Card */}
      <div className="bg-[#0e0e16] border rounded-xl p-6 mb-8"
        style={{ borderColor: isActive ? '#F59E0B30' : 'rgba(255,255,255,0.04)' }}>
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: isActive ? '#F59E0B15' : '#ffffff08' }}>
              <Buildings size={24} weight="duotone" style={{ color: isActive ? '#F59E0B' : '#444' }} />
            </div>
            <div>
              <p className="text-white font-bold text-lg">{isActive ? 'Publisher Standard' : 'No Active Plan'}</p>
              <p className="text-[12px] text-[#555]">
                {isActive ? '$20.00/mo — Invite-only publisher access' : 'Awaiting invite approval from CyBoard admin'}
              </p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase ${isActive ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#F59E0B]/10 text-[#F59E0B]'}`}
            data-testid="publisher-plan-status">
            {isActive ? 'Active' : sub?.status === 'none' ? 'Pending Invite' : sub?.status || 'Pending'}
          </span>
        </div>

        {isActive && sub?.invite_approved && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#10B981]/5 border border-[#10B981]/10 mb-4">
            <ShieldCheck size={16} className="text-[#10B981]" />
            <span className="text-[12px] text-[#10B981]">Invite approved — full publisher access granted</span>
          </div>
        )}

        {/* Feature Entitlements */}
        {isActive && (
          <div className="grid grid-cols-2 gap-3 mt-4">
            {features.map(f => {
              const ent = sub?.entitlements?.[f.feature_code];
              const enabled = ent?.value === 'true';
              return (
                <div key={f.feature_code} className="flex items-center gap-2">
                  {enabled ? <Check size={14} weight="bold" className="text-[#F59E0B]" /> : <X size={14} className="text-[#2a2a2a]" />}
                  <span className={`text-[12px] ${enabled ? 'text-[#999]' : 'text-[#333]'}`}>
                    {FEATURE_LABELS[f.feature_code] || f.feature_name}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {!isActive && (
          <div className="mt-4 p-4 rounded-lg bg-[#F59E0B]/5 border border-[#F59E0B]/10">
            <p className="text-[13px] text-[#F59E0B] font-medium">Publisher accounts are invite-only</p>
            <p className="text-[11px] text-[#888] mt-1">
              Your application is under review. Once approved by a CyBoard admin, your Publisher Standard plan will be activated automatically at $20/mo.
            </p>
          </div>
        )}
      </div>

      {/* Campaign Add-ons Section */}
      <div className="mb-8">
        <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'Unbounded' }}>Campaign Add-ons</h3>
        <p className="text-[12px] text-[#555] mb-5">
          {isActive ? 'Boost your games with premium promotional campaigns' : 'Available after your publisher plan is activated'}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ADDON_TYPES.map(addon => {
            const existing = addons.find(a => a.add_on_code === addon.code);
            return (
              <div key={addon.code}
                className="bg-[#0a0a12] border border-white/[0.04] rounded-xl p-4 flex items-start gap-3"
                data-testid={`addon-${addon.code}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#F59E0B]/10 flex-shrink-0">
                  <addon.icon size={18} weight="duotone" className="text-[#F59E0B]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-[13px] font-semibold">{addon.name}</p>
                  <p className="text-[11px] text-[#555] mt-0.5">{addon.desc}</p>
                  {existing ? (
                    <span className={`inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full font-medium ${existing.status === 'active' ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#F59E0B]/10 text-[#F59E0B]'}`}>
                      {existing.status === 'active' ? 'Active' : 'Requested'}
                    </span>
                  ) : (
                    <button
                      onClick={() => requestAddon(addon.code, addon.name)}
                      disabled={!isActive || requesting === addon.code}
                      className={`mt-2 flex items-center gap-1 text-[11px] font-semibold px-3 py-1.5 rounded-lg transition-all ${isActive ? 'bg-[#F59E0B]/10 text-[#F59E0B] hover:bg-[#F59E0B]/20' : 'bg-white/5 text-[#333] cursor-not-allowed'}`}
                      data-testid={`request-addon-${addon.code}`}>
                      {requesting === addon.code ? 'Requesting...' : 'Request'} <ArrowRight size={11} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Active Add-ons */}
      {addons.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>Your Add-ons</h3>
          <div className="space-y-2">
            {addons.map(a => (
              <div key={a.id} className="bg-[#0e0e16] border border-white/[0.04] rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-white text-[13px] font-medium">{a.name}</p>
                  <p className="text-[11px] text-[#555]">{a.add_on_code?.replace(/_/g, ' ')}</p>
                </div>
                <span className={`text-[10px] px-2.5 py-1 rounded-full font-medium ${a.status === 'active' ? 'bg-[#10B981]/10 text-[#10B981]' : a.status === 'pending' ? 'bg-[#F59E0B]/10 text-[#F59E0B]' : 'bg-white/5 text-[#555]'}`}>
                  {a.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
